import { REGIONS } from './constants';

/**
 * Here is where we check if the restaurant group is a regional location.
 *
 * @function
 * @param    {string} restaurantGroup    Location specific restaurant group name
 */
function containsLocationSpecific(restaurantGroup) {
    return Object.keys(REGIONS)
        .map((region) => region.toLowerCase())
        .includes(restaurantGroup.toLowerCase());
}
/**
 * Called to check if the item should display on the menu according to the allergen
 * filters.
 *
 * @function
 * @param    {object} allergens       item allergens from the API
 * @param    {array} allergenFilter  Allergens chosen in the filter.
 * @returns  {boolean}               returns true if there are no allergens
 */

function noAllergens({ allergens, allergenFilter }) {
    if (!allergenFilter) {
        return false;
    }
    let allergen = true;
    allergens.forEach((all) => {
        if (allergenFilter.HIDETRACES) {
            if (
                allergenFilter[(all || {}).name] === true &&
                all.present !== 'NO'
            ) {
                allergen = false;
            }
        } else {
            if (
                allergenFilter[(all || {}).name] === true &&
                all.present !== 'NO' &&
                all.present.indexOf('MAYBE') === -1
            ) {
                allergen = false;
            }
        }
    });
    return allergen;
}

/**
 * Called to check if the item should display on the menu according to the dietary constraints
 * filters.
 *
 * @function
 * @param    {array} diets           item dietary constraints array from the API
 * @param    {array} dietFilter      Dietary restraints chosen in the filter.
 * @returns  {boolean}               returns true if there are no dietary constraints to this item
 */
function noDietaryConstraints({ diets, dietFilter }) {
    if (!dietFilter.length) {
        return true;
    }

    //console.log('prop', allergens, filter);
    return dietFilter.every((item) => {
        return diets.indexOf(item) >= 0;
    })
        ? true
        : false;
}

/**
 * Some items exist in the API without any fulfilmentTypes - AKA they cannot be ordered. On the restricted menu, for
 * instance, both the salad section and the Nandinos section consist entirely of items that cannot be fulfilled. This
 * filter prevents the section itself from being displayed if all of its items are not purchasable.
 *
 * @function
 * @param    {[type]} data [description]
 * @returns  {[type]}      [description]
 */
function filterEmptySections(data) {
    return data.reduce((previousSection, currentSection) => {
        if (currentSection && currentSection.items) {
            const sectionHasItems = !!currentSection.items.filter((item) => {
                if (
                    item &&
                    item.fulfilmentTypes &&
                    item.fulfilmentTypes.length === 0
                ) {
                    return false;
                }
                return true;
            }).length;

            if (sectionHasItems) {
                previousSection.push(currentSection);
            }
        }
        return previousSection;
    }, []);
}

/**
 * Called to filter out restaurant groups that aren't either null (default item) or location specific items
 *
 * @function
 * @param    {object} data       menu from the API
 * @returns  {object}            returns default menu including location specific
 */
function filterRestaurantGroups(data) {
    return data.map((section) => ({
        ...section,
        items: section.items.filter(isTrial).map(mapMenuItem)
    }));
}

function filterOptions(option) {
    return (
        [null, ...Object.keys(REGIONS)].includes(option.restaurantGroup) &&
        (!option.restaurantGroup ||
            !option.restaurantGroup.toLowerCase().includes('trial'))
    );
}

function mapModifier(modifier) {
    return {
        ...modifier,
        options: modifier.options.filter(filterOptions)
    };
}

function mapMenuItem(menuItem) {
    return {
        ...menuItem,
        modifiers: menuItem.modifiers.map(mapModifier)
    };
}

function isTrial(item) {
    return (
        !item.flags.includes('IS_TRIAL') &&
        [null, ...Object.keys(REGIONS)].includes(item.restaurantGroup)
    );
}

/**
 * Called to filter out menu items that are unavailable either based on their start and end date or from fulfilment
 *
 * @function
 * @param    {object} data       menu from the API
 * @returns  {object}            returns default menu without items that aren't available yet (start date) and items no longer available (end date/fulfilment type)
 */
function filterUnavailable(data) {
    const date = new Date();

    return data.filter((section) => {
        section.items = section.items
            .filter((menuItem) => menuItem.fulfilmentTypes.length)
            .filter((menuItem) => {
                const isValidFrom = menuItem.startDate
                    ? date.getTime() >= new Date(menuItem.startDate).getTime()
                    : true;
                const isValidTo = menuItem.endDate
                    ? date.getTime() <= new Date(menuItem.endDate).getTime()
                    : true;
                return isValidFrom && isValidTo;
            });
        return section;
    });
}

/**
 * Sorts the menu by simple items (e.g. items without a subsection, secondary subsection or location specific restaurant group), restaurant groups, subsection and secondary subsection respectively
 *
 * @function
 * @param    {object} data       menu from the API
 * @returns  {object}            returns sorted menu
 */
function sortMenu(data) {
    return data.filter((section) => {
        // option 1 - no subsection and no secondary subsection and no restaurant group
        const mainItems = section.items.filter(
            (item) =>
                isEmpty(item.subsection) &&
                isEmpty(item.restaurantGroup) &&
                isEmpty(item.secondarySubsection)
        );

        const items = {
            items: mainItems || [],
            subsections: []
        };

        const subsectionItems = section.items.filter(
            (item) =>
                !isEmpty(item.subsection) ||
                !isEmpty(item.restaurantGroup) ||
                !isEmpty(item.secondarySubsection)
        );

        mapItemsToSubsections(items, subsectionItems);

        section.items = flattenItems(items);
        return section;
    });
}

/**
 * @function
 * @param {(string|null)} item section field containing either an empty string or null
 * @returns {boolean} returns true if the field is empty
 */
function isEmpty(item) {
    return !item || item.length === 0;
}

/**
 * Takes an array of item objects and puts each item into a subsection object (e.g. a subsection, secondary subsection or restaurant group)
 * @function
 * @param {object} allItems parent items object of all items for the section
 * @param {object[]} subsectionItems array of item objects
 */
function mapItemsToSubsections(allItems, subsectionItems) {
    subsectionItems.forEach((item) => {
        if (isEmpty(item.subsection)) {
            // option 2 - no subsection and no secondary subsection but a restaurant group
            if (!isEmpty(item.restaurantGroup)) {
                updateSubsections(
                    allItems.subsections,
                    item.restaurantGroup,
                    item
                );
                return;
            }
            return;
        }

        let search = item.subsection;

        if (isEmpty(item.secondarySubsection)) {
            // option 3 - a subsection and no secondary subsection and no restaurant group
            if (isEmpty(item.restaurantGroup)) {
                updateSubsections(allItems.subsections, search, item);
                return;
            }

            // option 4 - a subsection and no secondary subsection and a restaurant group
            search += `:${item.restaurantGroup}`;
            updateSubsections(allItems.subsections, search, item);
            return;
        }

        search += `:${item.secondarySubsection}`;

        // option 5 - a subsection and a secondary subsection and no restaurant group
        if (isEmpty(item.restaurantGroup)) {
            updateSubsections(allItems.subsections, search, item);
            return;
        }

        // option 6 - a subsection and a secondary subsection and a restaurant group
        search += `:${item.restaurantGroup}`;
        updateSubsections(allItems.subsections, search, item);
    });
}

/**
 * Called to update a subsection with the new item if it can be found otherwise a new subsection is created
 * @param {object} allItems parent items object of all items for the section
 * @param {string} search the subsection to find within allItems
 * @param {object} item current item to be added to subsection
 * @returns
 */
function updateSubsections(subsections, search, item) {
    const searchTermsArray = search.split(':');
    const searchTerm = searchTermsArray[0];

    const subsection = subsections.find(
        (subsect) => subsect.name === searchTerm
    );

    if (!subsection) {
        createSubsection(searchTerm, subsections);
    }

    const nextSearch = search.replace(/^.+?(:)/, '');
    const nextSubsection = subsections.find(
        (subsect) => subsect.name === searchTerm
    );
    if (nextSearch && nextSearch !== searchTerm) {
        updateSubsections(nextSubsection.subsections, nextSearch, item);
    } else {
        nextSubsection.items.push(item);
    }
}

/**
 * Creates a new subsection and does 1 of 3 things on the subsections array:
 * 1) append the new subsection if the subsection name isn't regional
 * 2) preprend the new subsection if it is the first regional subsection
 * 3) insert regional subsections after existing regional subsections but before non-regional subsections
 * @param {string} searchTerm
 * @param {object[]} subsections
 */
function createSubsection(searchTerm, subsections) {
    const newSubsection = {
        name: searchTerm,
        items: [],
        subsections: []
    };
    const index = subsections.findIndex((subsect) =>
        containsLocationSpecific(subsect.name)
    );

    if (containsLocationSpecific(searchTerm)) {
        if (index === -1) {
            subsections.unshift(newSubsection);
        } else {
            subsections.splice(index + 1, 0, newSubsection);
        }
    } else {
        subsections.push(newSubsection);
    }
}

/**
 * Takes an object containing nested arrays of items and flattens to one array
 * @param {object} allItems parent items object of all items for the section
 * @returns flat array of all items
 */
function flattenItems(allItems) {
    let result = [];

    if (allItems.items.length) {
        result = [...result, ...allItems.items];
    }

    if (allItems.subsections.length) {
        allItems.subsections.forEach((subsection) => {
            result = [...result, ...flattenItems(subsection)];
        });
    }
    return result;
}

/**
 * Called to organise the menu by filtering out items not used and sorting by subsection
 *
 * @function
 * @param    {object} data       menu from the API
 * @returns  {object}            returns default menu without items that aren't available to everyone
 */
function organiseMenu(data) {
    data = filterRestaurantGroups(data);
    data = filterUnavailable(data);
    data = filterEmptySections(data);
    data = sortMenu(data);
    return data;
}

export {
    noAllergens,
    noDietaryConstraints,
    containsLocationSpecific,
    organiseMenu,
    filterUnavailable,
    sortMenu,
    isEmpty,
    updateSubsections,
    createSubsection,
    flattenItems,
    filterOptions,
    mapModifier,
    mapMenuItem,
    isTrial,
    filterRestaurantGroups
};
